import { Button, ClickAwayListener, Divider, IconButton, Portal } from '@mui/material';
import { NiaraIcon } from '@niaratech/niara-icons';
import { addDays, addMonths, diffInDays, formatDate, printPrice } from '@niaratech/niara-js-commons';
import { useImmutableFunction, useIsMobile } from '@niaratech/niara-react-components';
import startOfMonth from 'date-fns/startOfMonth';
import _times from 'lodash/times';
import _upperFirst from 'lodash/upperFirst';
import React, { MouseEventHandler } from 'react';
import ModalOTA, { ModalOTAProps } from '../../components/ModalOTA';
import useDefaultPortalContext from '../../portal/useDefaultPortalContext';
import MonthCalendar, { MonthCalendarProps } from '../MonthCalendar';
import * as S from './styles';
export const CALENDAR_MODE_KEY = 'otabuilder-calendarMode';
export type DatePickerValue = {
  startDate?: string;
  endDate?: string;
};
const DAYS_OF_WEEK = (() => {
  const a = [];
  const exampleSunday = new Date(2019, 8, 8);
  for (let i = 0; i < 7; i++) {
    a.push(addDays(exampleSunday, i));
  }
  return a;
})();
export type DatePickerV3Props = {
  min?: string | undefined;
  max?: string | undefined;
  minDate?: string | undefined;
  maxDate?: string | undefined;
  minNightCount?: number;
  maxNightCount?: number;
  value?: DatePickerValue;
  setValue?: React.Dispatch<React.SetStateAction<DatePickerValue>>;
  errors?: string[];
  referenceComponent?: React.MutableRefObject<HTMLElement>;
  detailsToCalendar?: MonthCalendarProps['detailsToCalendar'];
  /**
   * carregando detailsToCalendar - mostra indicador nos dias
   */
  loading?: boolean;
  onShownMonthChange?: {
    (date: string);
  };
  initOpened?: boolean;
  onShown?: {
    ();
  };
};
const DatePickerV3: React.FC<DatePickerV3Props> = ({
  value,
  setValue,
  errors,
  detailsToCalendar,
  loading,
  minNightCount = 1,
  maxNightCount,
  referenceComponent,
  onShownMonthChange: _propsOnShownMonthChange,
  initOpened,
  onShown,
  ...props
}) => {
  const min = props?.min ?? props?.minDate;
  const max = props?.max ?? props?.maxDate;
  const isMobile = useIsMobile();
  const [openDate, setOpenDate] = React.useState(() => initOpened ?? false);
  const [startingMonth, setStartingMonth] = React.useState<string>(() => {
    return formatDate(startOfMonth(new Date()), undefined, 'yyyy-MM-dd');
  });
  const [startingMonthDesktop, setStartingMonthDesktop] = React.useState<string>(() => {
    if (value?.startDate) {
      return value?.startDate;
    }
    return formatDate(startOfMonth(new Date()), undefined, 'yyyy-MM-dd');
  });
  const [monthCount, setMonthCount] = React.useState(() => 4);
  const nightCount = Boolean(value?.startDate) && Boolean(value?.endDate) && diffInDays(value?.endDate, value?.startDate) || undefined;
  const isValidValue = nightCount > 0;

  // centralizar popper
  const datePickerRef = React.useRef(null);
  React.useEffect(() => {
    const datePickerCurrent = datePickerRef?.current;
    // Calcula a posição left de onde deve iniciar o componente do calendário para ele ficar centralizado com o componente referencia passado.
    // O left = 0 é o início do campo do calendário, ou seja, para calcular o meio o left ficará negativo
    // Se não passar o componente referência, ele não ficará centralizado com nada e ficará alinhado à esquerda do campo do calendário
    // Isso é utilizado apenas para o modo desktop
    if (!openDate || isMobile) return;
    if (referenceComponent && referenceComponent.current && datePickerCurrent) {
      if (!datePickerRef.current?.getBoundingClientRect?.()) return;
      const referenceRect = referenceComponent.current?.getBoundingClientRect();
      const calendarRect = datePickerCurrent.getBoundingClientRect();
      const sizeDiff = (calendarRect.width - referenceRect.width) / 2;
      const leftTranslate = calendarRect.left - referenceRect.left;
      let leftPosition = -(leftTranslate + sizeDiff);
      const exceededLeftScreen = referenceRect.left - sizeDiff;
      if (exceededLeftScreen < 0) leftPosition -= exceededLeftScreen;
      const originalLeft = datePickerCurrent.style.left;
      datePickerCurrent.style.left = `${leftPosition}px`;
      return () => {
        datePickerCurrent.style.left = originalLeft;
      };
    }
  }, [referenceComponent, openDate, isMobile]);

  // efeitos onShown e onShownMonthChange
  React.useEffect(() => {
    if (openDate && onShown) {
      onShown();
    }
    // não disparar onShown se mudar a instância
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDate]);

  // efeitos onShown e onShownMonthChange (desktop)
  React.useEffect(() => {
    if (onShownMonthChange) {
      onShownMonthChange(startingMonthDesktop);
    }
    // não disparar onShown se mudar a instância
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startingMonthDesktop]);

  // manipulação do mês pelos botões:
  const startDate = value?.startDate;
  const endDate = value?.endDate;
  const minStartDate = !min ? null : !minNightCount ? min : addDays(min, minNightCount);
  const maxStartDate = !max ? null : !maxNightCount ? addDays(max, -1) : addDays(max, maxNightCount);
  const startDateCanBeReduced = !startDate ? false : !minStartDate ? true : startDate > minStartDate;
  const startDateCanBeIncreased = !startDate ? false : !maxStartDate ? true : startDate < maxStartDate;
  const minEndDate = !min ? null : !minNightCount ? min : addDays(min, minNightCount);
  const maxEndDate = max;
  const endDateCanBeReduced = !endDate ? false : !minEndDate ? true : endDate > minEndDate;
  const endDateCanBeIncreased = !endDate ? false : !maxEndDate ? true : endDate < maxEndDate;
  const handleDayButtonClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    const operation = event.currentTarget.dataset?.['op'];
    if (operation) {
      let newStartDate = startDate;
      let newEndDate = endDate;
      switch (operation) {
        case 'START_DATE_SUBTRACT_ONE':
          if (!startDateCanBeReduced) break;
          newStartDate = addDays(startDate, -1);
          break;
        case 'START_DATE_ADD_ONE':
          if (!startDateCanBeIncreased) break;
          newStartDate = addDays(startDate, 1);
          if (minNightCount > 0 && diffInDays(endDate, startDate) <= minNightCount) {
            newEndDate = addDays(newStartDate, minNightCount);
          }
          break;
        case 'END_DATE_SUBTRACT_ONE':
          if (!endDateCanBeReduced) break;
          if (diffInDays(endDate, startDate) > minNightCount) {
            newEndDate = addDays(endDate, -1);
          } else {
            newEndDate = addDays(endDate, -1);
            newStartDate = addDays(newEndDate, -minNightCount);
          }
          break;
        case 'END_DATE_ADD_ONE':
          if (!endDateCanBeIncreased) break;
          newEndDate = addDays(endDate, 1);
          break;
        default:
          break;
      }
      setValue({
        startDate: newStartDate,
        endDate: newEndDate
      });
    }
  };
  const lowestPrice = React.useMemo(() => {
    if (detailsToCalendar?.length > 0) return [...detailsToCalendar].sort((oldValue, newValue) => (oldValue?.value ?? 0) - (newValue?.value ?? 0)).filter(i => i.value != null)[0];else return;
  }, [detailsToCalendar]);

  // MOBILE: posiciona o scroll no startDate e dispara a função onShownDateChange ao navegar entre os meses
  const modalRef = React.useRef<HTMLDivElement>();
  const onShownMonthChange = useImmutableFunction(_propsOnShownMonthChange);
  /**
   * Ao abrir o modal de data (mobile) dar scroll até o início do mês selecionado.
   * (onHidden é chamado também quando modal é mostrado - nesse momento modalRef está disponível)
   */
  const onModalHidden: ModalOTAProps['onHidden'] = React.useCallback(hidden => {
    const modalScrollableContainer = modalRef.current && modalRef.current.querySelector('.modalOTA-content');
    if (!hidden && modalScrollableContainer) {
      // Código que move o modal até o mês do primeiro dia selecionado
      const startDateElement = modalScrollableContainer.querySelector('.niara-month-calendar-button.start-date');
      const scrollOffset = /* altura do cabeçalho do modal: 64px */64 + /* altura do cabeçalho do datepicker com dias da semana: 46px */46 + /* altura do nome do mês + margem: ~30px */30 + /* margem entre dias da semana + nome do mês: ~20px */20;
      // /* seletor data / mês: 90px */ (enableDateAndMonthSelector ? 90 : 0)
      if (startDateElement) {
        const startingDaysElement = startDateElement.closest('.niara-month-calendar-grid');
        const modalContainerRect = modalScrollableContainer.getBoundingClientRect();

        // Calcular a posição do startDateElement relativa ao modal
        modalScrollableContainer.scrollTop = startingDaysElement.getBoundingClientRect().top - modalContainerRect.top - scrollOffset;
      }

      // Código para colocar event handler de scroll que chama onShownDateChange se existir a função immutableonShownDateChange. Também cria novos calendários
      // Considerar que data está sendo exibida quando a parte de topo de baixo do mês aparece toda no viewport (sob o botão de aplicar)
      let lastIdx = null;
      const allIndexSearched: number[] = [];
      // Código para colocar event handler de scroll que chama onShownDateChange se existir a função immutableonShownDateChange
      const onScroll = () => {
        const monthBlocks = modalScrollableContainer.querySelectorAll('.niara-month-calendar-grid');
        // qual dos rdrDays está sendo exibido?
        const currentScrollTop = modalScrollableContainer.scrollTop;
        for (let i = 0; i < monthBlocks.length; i++) {
          const monthBlock = monthBlocks[i];
          if (monthBlock.getBoundingClientRect().top - modalScrollableContainer.getBoundingClientRect().top + modalScrollableContainer.scrollTop >= currentScrollTop + scrollOffset -
          /*Necessária para conseguir mudar para o primeiro indice nos casos do modal de data do long stay, porque nesse modal não existe o header com togglo de data e mês.
           */
          130) {
            if (i !== lastIdx && !allIndexSearched.includes(i)) {
              lastIdx = i;
              if (onShownMonthChange) {
                onShownMonthChange(addMonths(startingMonth, i));
              }
              allIndexSearched.push(i);
            }
            break;
          }
        }

        // segundo loop - verifica o último mês visível, para criar novos calendários
        for (let i = monthBlocks.length - 1; i >= 0; i--) {
          const monthBlock = monthBlocks[i];
          if (monthBlock.getBoundingClientRect().top < modalScrollableContainer.getBoundingClientRect().bottom) {
            // cria calendários de mais meses conforme faz o scroll
            setMonthCount(currentCount => {
              if (currentCount >= 24) return currentCount;
              if (currentCount <= i + 1) {
                return Math.min(currentCount + 4, 24);
              } else {
                return currentCount;
              }
            });
          }
        }
      };
      // Força a execução inicial do scroll handler
      onScroll();
      modalScrollableContainer.addEventListener('scroll', onScroll);
      // Não precisa remover o eventListener 'scroll' pois, ao fechar e abrir o modal novamente o modalContainer será destruído
      // Atenção - se mudar a implementação do Modal, isso pode não ser mais verdade.
    }
  }, [onShownMonthChange, startingMonth]);
  const portalContainer = useDefaultPortalContext()?.container;
  return <S.DatePickerContainer>
      <button type="button" className="date-picker-input" onClick={() => {
      if (!openDate && value?.startDate) {
        // move o calendário (desktop) para a data inicial
        setStartingMonthDesktop(value?.startDate);
      }
      setOpenDate(true);
    }}>
        <div className="date-picker-input-content">
          <div className="start-date date">
            <div className="date">{formatDate(value?.startDate, undefined, 'PP')}</div>
            <button type="button" className="btn-decrease btn-decrease-start-date" disabled={!startDateCanBeReduced} onClick={handleDayButtonClick} data-op="START_DATE_SUBTRACT_ONE">
              <NiaraIcon icon="sysChevronLeft" size={16} />
            </button>
            <button type="button" className="btn-increase btn-increase-start-date" disabled={!startDateCanBeIncreased} onClick={handleDayButtonClick} data-op="START_DATE_ADD_ONE">
              <NiaraIcon icon="sysChevronRight" size={16} />
            </button>
          </div>
          <div className="end-date date">
            <div className="date">{formatDate(value?.endDate, undefined, 'PP')}</div>
            <button type="button" className="btn-decrease btn-decrease-end-date" disabled={!endDateCanBeReduced} onClick={handleDayButtonClick} data-op="END_DATE_SUBTRACT_ONE">
              <NiaraIcon icon="sysChevronLeft" size={16} />
            </button>
            <button type="button" className="btn-increase btn-increase-end-date" disabled={!endDateCanBeIncreased} onClick={handleDayButtonClick} data-op="END_DATE_ADD_ONE">
              <NiaraIcon icon="sysChevronRight" size={16} />
            </button>
          </div>
        </div>
      </button>
      {errors?.length > 0 && <div className="date-picker-errors">{errors?.join(', ')}</div>}

      {/* no mobile mostra modal */}
      {isMobile && <Portal container={portalContainer}>
          <ModalOTA stickyHeader show={openDate} setShow={setOpenDate} desktopVariant="none" mobileVariant="full" padding="0" modalRef={modalRef} onHidden={onModalHidden} title={<>Selecione suas datas</>} zIndex={2147483647} render={({
        hide
      }) => <S.ModalContent>
                <div className="date-picker-calendar-weekdays">
                  <div className="date-picker-calendar-weekdays-container">
                    {DAYS_OF_WEEK.map(d => formatDate(d, undefined, 'EEEEEE')).map(dayOfWeek => <div className="date-picker-calendar-weekday" key={dayOfWeek}>
                        {dayOfWeek}
                      </div>)}
                  </div>
                </div>
                <div className="date-picker-calendar-container">
                  {_times(monthCount, i => {
            const currentMonth = addMonths(startingMonth, i);
            return <MonthCalendar value={value} setValue={setValue} currentMonth={currentMonth} key={formatDate(currentMonth, undefined, 'yyyy-MM')} min={min} max={max} detailsToCalendar={detailsToCalendar} loading={loading} />;
          })}
                </div>
                <div className="date-picker-calendar-modal-footer">
                  <div className="dates">
                    {Boolean(value?.startDate) && _upperFirst(formatDate(value.startDate, undefined, 'EEEEEE, d MMM'))}{' '}
                    - {Boolean(value?.endDate) && _upperFirst(formatDate(value.endDate, undefined, 'EEEEEE, d MMM'))}
                    {nightCount > 1 ? <small className="nightCount">({nightCount} noites)</small> : nightCount > 0 ? <small className="nightCount">({nightCount} noite)</small> : null}
                  </div>
                  <Button variant="contained" color="primary" fullWidth disabled={!isValidValue} onClick={hide}>
                    Aplicar
                  </Button>
                </div>
              </S.ModalContent>} />
        </Portal>}
      {/* no desktop, mostra popper flutuante */}
      {!isMobile && <div className={'date-picker-calendar-popper ' + (openDate ? 'open ' : '')} ref={datePickerRef}>
          {openDate && <ClickAwayListener onClickAway={() => {
        setOpenDate(false);
      }}>
              <div className="date-picker-calendar-popper-contents">
                <div className="months">
                  <IconButton className="prev-month" onClick={() => {
              setStartingMonthDesktop(c => addMonths(c, -1));
            }}>
                    <NiaraIcon icon="sysChevronLeft" size={24} />
                  </IconButton>
                  <IconButton className="next-month" onClick={() => {
              setStartingMonthDesktop(c => addMonths(c, 1));
            }}>
                    <NiaraIcon icon="sysChevronRight" size={24} />
                  </IconButton>
                  <div className="left-month">
                    <MonthCalendar value={value} setValue={setValue} onPeriodSet={() => setOpenDate(false)} currentMonth={startingMonthDesktop} min={min} max={max} detailsToCalendar={detailsToCalendar} loading={loading} />
                  </div>
                  <div className="right-month">
                    <MonthCalendar value={value} setValue={setValue} onPeriodSet={() => setOpenDate(false)} currentMonth={addMonths(startingMonthDesktop, 1)} min={min} max={max} detailsToCalendar={detailsToCalendar} loading={loading} />
                  </div>
                </div>
                {detailsToCalendar?.length > 0 && <div className="captions">
                    {/* {enableDateAndMonthSelector && (
                     <div className="caption">
                      <p>
                        Estadia mínima de <b> 30 noites</b>
                      </p>
                     </div>
                     )} */}
                    {lowestPrice?.value > 0 && <>
                        <div className="caption">
                          Diárias a partir de:{' '}
                          <span className="text-bold">{printPrice(lowestPrice.value, lowestPrice.currency)}</span>
                        </div>
                        <Divider orientation="vertical" flexItem />
                      </>}
                    <div className="caption">
                      <span className="unavailable-icon">x</span>
                      <span>Sem disponibilidade</span>
                    </div>
                    <Divider orientation="vertical" flexItem />
                    <div className="caption">
                      <span className="min-nights">x</span>
                      <span>Mínimo de noites</span>
                    </div>
                  </div>}
              </div>
            </ClickAwayListener>}
        </div>}
    </S.DatePickerContainer>;
};
export default DatePickerV3;